package fr.frux.feature.map

import fr.frux.core.common.error.AppError
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.model.Tree

/**
 * Etat apres un lot detaille recu avec succes, sorti de [MapViewModel] (ratchet detekt `LargeClass`).
 * [emptyArea] et [emptyAreaSilenced] arrivent calcules : le second depend de la memoire de zone,
 * que seul le ViewModel tient.
 */
internal fun MapState.withNearbyBatch(
    trees: List<Tree>,
    requestedFilter: MapFilter,
    radiusKm: Double,
    truncatedTotal: Int?,
    emptyArea: FilteredNoResults?,
    emptyAreaSilenced: Boolean,
): MapState {
    val visibleTrees = visibleUnderRollup(trees, selectedSpeciesRollupId, speciesRollup)
    // Un arbre absent du nouveau lot ne peut plus etre selectionne : la
    // fiche resterait ouverte sur un marqueur disparu de la carte (#125).
    val selectionSurvives = visibleTrees.any { tree -> tree.id == selectedTreeId }
    val textSearchActive = selectedSpeciesRollupId == null
    return copy(
        nearbyTrees = trees,
        // Une requete de filtre remplace le lot source : conserver les
        // resultats textuels calcules sur l'ancien lot ferait reapparaitre
        // des arbres que le serveur vient d'exclure.
        searchResults = if (textSearchActive) matchingTrees(searchQuery, visibleTrees) else emptyList(),
        speciesSuggestions = if (textSearchActive) matchingSpecies(searchQuery, species) else emptyList(),
        selectedTreeId = selectedTreeId.takeIf { selectionSurvives },
        isLoading = false,
        canSearchArea = false,
        isSearchingArea = false,
        error = null,
        truncatedTotal = truncatedTotal,
        // Mesure sur visibleTrees, pas le lot brut : une selection
        // d'espece peut vider l'ecran alors que le serveur a
        // repondu plein, c'est bien « aucun figuier par ici ».
        filteredNoResults = filteredNoResultsFor(visibleTrees, requestedFilter, selectedSpeciesRollupId, radiusKm),
        emptyArea = emptyArea,
        emptyAreaSilenced = emptyAreaSilenced,
        // Tout succes efface le bandeau : soit c'est le reseau qui
        // vient de repondre (donnees fraiches), soit c'est le cache
        // qui vient d'etre servi et le reseau n'a pas encore parle.
        // Dans les deux cas rien ne justifie encore le bandeau.
        showingStaleCache = false,
    )
}

/** Etat apres l'echec du chargement d'un lot detaille, meme raison d'etre que [withNearbyBatch]. */
internal fun MapState.withNearbyFailure(
    error: AppError,
    displayingStaleCache: Boolean,
): MapState =
    copy(
        isLoading = false,
        // Un echec doit laisser retenter : la vue est toujours
        // perimee, et la pastille est le seul chemin pour y revenir
        // sans redeplacer la carte.
        canSearchArea = isSearchingArea || canSearchArea,
        isSearchingArea = false,
        error = error,
        // N'arme le bandeau que si le lot affiche vient reellement
        // du cache perime (#62) : un echec sans emission cache
        // prealable ne doit rien afficher de plus que l'erreur.
        showingStaleCache = displayingStaleCache,
    )
